import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "../types";
import { api, ApiError } from "../services/api";
import { clearSession, getSession, setSession } from "../services/session";
import { BACKEND_ENABLED } from "../config";

interface AuthState {
  session: Session | null;
  /** True while we revalidate a stored token on first load. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<Session | null>(() =>
    BACKEND_ENABLED ? getSession() : null
  );
  const [loading, setLoading] = useState<boolean>(
    () => BACKEND_ENABLED && getSession() !== null
  );

  // On first load, if we have a stored token, confirm it's still valid.
  useEffect(() => {
    let cancelled = false;
    // No backend (static public build) → no session, no revalidation call.
    if (!BACKEND_ENABLED || !getSession()) {
      setLoading(false);
      return;
    }
    api
      .me()
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          clearSession();
          if (!cancelled) setSessionState(null);
        }
        // Network/other errors: keep the optimistic session, don't lock the user out.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    setSessionState(setSession(res));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* best-effort; clear locally regardless */
    }
    clearSession();
    setSessionState(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ session, loading, login, logout }),
    [session, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
